import { getLang, locale } from '../i18n';

/**
 * Formatierung nach Oberflächensprache (i18n/index.ts):
 * de-DE wie im Design: `1.234,5 km`, `5:46 /km`, `2:41:30`, `Sa, 29.08.2026`, `~205 hrTSS`;
 * en-GB: `1,234.5 km`, `5:46 /km`, `2:41:30`, `Sat, 29 Aug 2026`.
 */
const cache = new Map<string, Intl.NumberFormat>();
const nf = (digits: number) => {
  const k = `${locale()}|${digits}`;
  let f = cache.get(k);
  if (!f) cache.set(k, (f = new Intl.NumberFormat(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })));
  return f;
};

export const num = (v: number, digits = 0) => nf(digits).format(v);
export const km = (m: number | null | undefined, digits = 1) => (m == null ? '–' : `${nf(digits).format(m / 1000)} km`);

/** Zahl mit bis zu `max` Nachkommastellen (ohne Nullen aufzufüllen) – z. B. für Chart-Achsen. */
export const numMax = (v: number, max = 2) => new Intl.NumberFormat(locale(), { maximumFractionDigits: max }).format(v);

/** Prozent aus einem Anteil 0–1: `42 %` (de) bzw. `42%` (en). */
export const pct = (share: number, digits = 0) => (getLang() === 'en' ? `${num(share * 100, digits)}%` : `${num(share * 100, digits)} %`);

/**
 * US-Dollar mit zwei Nachkommastellen: `4,55 $` (de) bzw. `$4.55` (en).
 * Beträge unter einem Cent (ein einzelner Kommentar) als `< 0,01 $`, damit
 * nichts Verbrauchtes wie null aussieht.
 */
export function usd(v: number): string {
  const tiny = v > 0 && v < 0.005;
  const s = nf(2).format(tiny ? 0.01 : v);
  const amount = getLang() === 'en' ? `$${s}` : `${s} $`;
  return tiny ? `< ${amount}` : amount;
}

export function duration(s: number | null | undefined): string {
  if (s == null) return '–';
  // Erst auf ganze Sekunden runden, sonst entsteht „10:60".
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

export function hoursMinutes(s: number): string {
  const total = Math.round(s / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')} h`;
}

/** Tempo je nach Sportart: Laufen/Gehen/Wandern min/km, Rad km/h, Schwimmen min/100 m. */
export function pace(speed: number | null | undefined, sport: string): string {
  if (!speed) return '–';
  if (sport === 'ride') return `${nf(1).format(speed * 3.6)} km/h`;
  const per = sport === 'swim' ? 100 : 1000;
  const s = per / speed;
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')} ${sport === 'swim' ? '/100 m' : '/km'}`;
}

/* --- Kalendernamen ------------------------------------------------------ */

const WD: Record<'de' | 'en', string[]> = {
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};
const WD_LONG: Record<'de' | 'en', string[]> = {
  de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
};
const MON: Record<'de' | 'en', string[]> = {
  de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};
const MON_LONG: Record<'de' | 'en', string[]> = {
  de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

/** Wochentag kurz, 0 = Sonntag (wie `Date#getDay`): `Sa` / `Sat`. */
export const weekdayShort = (day: number) => WD[getLang()][day]!;
export const weekdayLong = (day: number) => WD_LONG[getLang()][day]!;
/** Montag zuerst (ISO-Woche): `['Mo', … 'So']` bzw. `['Mon', … 'Sun']`. */
export const weekdaysMonFirst = (long = false) => [1, 2, 3, 4, 5, 6, 0].map(long ? weekdayLong : weekdayShort);
/** Monat kurz, 0 = Januar: `Mär` / `Mar`. */
export const monthShort = (month: number) => MON[getLang()][month]!;
export const monthLong = (month: number) => MON_LONG[getLang()][month]!;

const parts = (local: string) => {
  const [d] = local.split(' ');
  const [y, mo, da] = d!.split('-').map(Number);
  return { y: y!, mo: mo!, da: da!, wd: new Date(Date.UTC(y!, mo! - 1, da!)).getUTCDay() };
};

/**
 * `YYYY-MM-DD[ HH:MM:SS]` als Ortszeit lesen, ohne Zeitzonen-Umrechnung.
 * de: `Sa, 29.08.2026` / `Sa, 29.08.` · en: `Sat, 29 Aug 2026` / `Sat, 29 Aug`.
 */
export function dateLabel(local: string, withYear = true): string {
  const { y, mo, da, wd } = parts(local);
  if (getLang() === 'en') return `${WD.en[wd]}, ${da} ${MON.en[mo - 1]}${withYear ? ` ${y}` : ''}`;
  return `${WD.de[wd]}, ${String(da).padStart(2, '0')}.${String(mo).padStart(2, '0')}.${withYear ? y : ''}`;
}

/** Datum ohne Wochentag: `29.08.2026` / `29 Aug 2026` (bzw. ohne Jahr `29.08.` / `29 Aug`). */
export function shortDate(local: string, withYear = true): string {
  const { y, mo, da } = parts(local);
  if (getLang() === 'en') return `${da} ${MON.en[mo - 1]}${withYear ? ` ${y}` : ''}`;
  return `${String(da).padStart(2, '0')}.${String(mo).padStart(2, '0')}.${withYear ? y : ''}`;
}

/** Monat und Jahr: `August 2026` (beide Sprachen gleich gebaut). */
export function monthYear(local: string): string {
  const { y, mo } = parts(local);
  return `${monthLong(mo - 1)} ${y}`;
}

/** Monatsname aus `YYYY-MM[-DD]`: `November`. */
export const monthOf = (ym: string) => monthLong(Number(ym.slice(5, 7)) - 1);

export const timeLabel = (local: string) => local.split(' ')[1]?.slice(0, 5) ?? '';

export const load = (v: number | null | undefined, estimated: boolean | number) =>
  v == null ? '–' : `${estimated ? '~' : ''}${Math.round(v)}`;

/** „vor 5 min" / „5 min ago" usw. für Zeitstempel in ms. */
export function agoMs(ms: number): string {
  const en = getLang() === 'en';
  const min = Math.round((Date.now() - ms) / 60000);
  if (min < 1) return en ? 'just now' : 'gerade eben';
  if (min < 60) return en ? `${min} min ago` : `vor ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return en ? `${h} h ago` : `vor ${h} h`;
  const d = Math.round(h / 24);
  return en ? `${d} days ago` : `vor ${d} Tagen`;
}
